import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

export type PopoverRadioOption<T> = {
  value: T;
  label: string;
  description?: string;
};

export default function PopoverRadioMenu<T extends string | null>({
  value,
  options,
  onChange,
  summary,
  menuTitle,
  triggerContent,
  triggerClassName,
  triggerTitle,
  triggerAriaLabel,
  testIdPrefix,
  idSuffix,
  open,
  onOpenChange,
  className = "absolute right-1 top-1 z-20",
}: {
  value: T;
  options: PopoverRadioOption<T>[];
  onChange: (value: T) => void;
  summary: string;
  menuTitle?: string;
  triggerContent: React.ReactNode;
  triggerClassName?: string;
  triggerTitle?: string;
  triggerAriaLabel?: string;
  testIdPrefix: string;
  idSuffix?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const suffix = idSuffix ? `-${idSuffix}` : "";

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const top = rect.height > 0 ? rect.bottom + 4 : 80;
      const right =
        rect.width > 0 ? Math.max(8, window.innerWidth - rect.right) : 16;
      setMenuStyle({
        position: "fixed",
        top,
        right,
      });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      onOpenChange(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    // Defer so the opening click cannot immediately close the menu.
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("keydown", onKeyDown);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onOpenChange]);

  return (
    <div
      ref={rootRef}
      className={className}
      onPointerDownCapture={(event) => event.stopPropagation()}
      onMouseDownCapture={(event) => event.stopPropagation()}
    >
      <button
        ref={triggerRef}
        type="button"
        title={triggerTitle ?? summary}
        aria-label={triggerAriaLabel ?? `${summary}. Open menu`}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={`${testIdPrefix}-menu${suffix}`}
        className={triggerClassName}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange(!open);
        }}
      >
        {triggerContent}
      </button>
      {open ? (
        <div
          role="menu"
          data-testid={`${testIdPrefix}-dropdown${suffix}`}
          style={menuStyle}
          className="fixed z-[80] w-52 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {menuTitle ? (
            <p className="border-b border-slate-100 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {menuTitle}
            </p>
          ) : null}
          {options.map((option) => {
            const selected = value === option.value;
            const optionKey = String(option.value);
            return (
              <button
                key={optionKey}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                title={option.description}
                data-testid={`${testIdPrefix}-option-${option.value ?? "none"}${suffix}`}
                className={`flex w-full flex-col px-2.5 py-1.5 text-left hover:bg-slate-50 ${
                  selected ? "bg-violet-50" : ""
                }`}
                onClick={() => {
                  onChange(option.value);
                  onOpenChange(false);
                }}
              >
                <span
                  className={`text-xs font-semibold ${
                    selected ? "text-violet-800" : "text-slate-800"
                  }`}
                >
                  {option.label}
                </span>
                {option.description ? (
                  <span className="text-[10px] leading-snug text-slate-500">
                    {option.description}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
